import Head from 'next/head';
import { renderMetaTags, useQuerySubscription } from 'react-datocms';
import { PageIntro, Layout, PersonSummary, PageHero, GeneralPageBody, PageCTA, Author, Container } from 'components';
import { request } from 'lib/datocms';
import { getSchema } from 'lib/getSchema';
import {
  metaTagsFragment,
  pageHeroImageFragment,
  responsiveImageFragment,
  menuFragment,
  contentBlocks,
  pageCTAFragment,
  contentFragments,
  serviceIconFragment,
} from 'lib/fragments';

export async function getStaticPaths() {
  const data = await request({ query: `{ allServicePages(first: 100) { slug } }` });

  return {
    paths: data.allServicePages.map((page) => `/services/${page.slug}`),
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params, preview = false }) {
  const graphqlRequest = {
    query: `
      query TeamMemberBySlug($slug: String) {
        menu {
          ...menuFragment
        }
        servicePage(filter: {slug: {eq: $slug}}) {
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
          title
          introduction
          ${pageHeroImageFragment}
          slug
          content {
            ${contentBlocks}
          }
          tagIcon {
            ${serviceIconFragment}
          }
          teamMember {
            name
            slug
            linkedIn
            jobTitle
            image {
              responsiveImage {
                ...responsiveImageFragment
              }
            }
            contactDetailsImage {
              responsiveImage {
                ...responsiveImageFragment
              }
            }
          }
          callToAction {
            ...pageCTAFragment
          }
        }
      }

      ${metaTagsFragment}
      ${responsiveImageFragment}
      ${contentFragments}
      ${menuFragment}
      ${pageCTAFragment}
    `,
    preview,
    variables: {
      slug: params.slug,
    },
  };

  const initialData = await request(graphqlRequest);

  if (!initialData.servicePage) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData: await request(graphqlRequest),
            token: process.env.NEXT_DATOCMS_API_TOKEN,
          }
        : {
            enabled: false,
            initialData: await request(graphqlRequest),
          },
    },
    revalidate: 120,
  };
}

export default function ServicePage({ subscription }) {
  const {
    data: { servicePage, menu },
  } = useQuerySubscription(subscription);

  const metaTags = servicePage.seo;
  const callToAction = servicePage.callToAction;
  const heroImage = servicePage.image;
  const schema = getSchema(servicePage.slug);

  return (
    <Layout preview={subscription.preview} menu={menu}>
      {metaTags && <Head>
        {renderMetaTags(metaTags)}
        {schema && <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 2) }}
        />}
      </Head>}

      {heroImage && <PageHero responsiveImage={heroImage.responsiveImage} />}

      <PageIntro title={servicePage.title} introduction={servicePage.introduction} serviceIcon={servicePage.tagIcon} />

      <section className="PageBody PageBody--services">
        <GeneralPageBody content={servicePage.content} />

        {servicePage.teamMember && (
          <Container>
            <Author
              name={servicePage.teamMember.name}
              contactDetailsImage={servicePage.teamMember.contactDetailsImage.responsiveImage}
              linkedIn={servicePage.teamMember.linkedIn}
              responsiveImage={servicePage.teamMember.image.responsiveImage}
              jobTitle={servicePage.teamMember.jobTitle}
            />
          </Container>
        )}
      </section>

      {callToAction && (
        <PageCTA
          title={callToAction.title}
          caption={callToAction.caption}
          btnLabel={callToAction.buttonLabel}
          btnURL={callToAction.buttonUrl}
        />
      )}
    </Layout>
  );
}
